import { Box, Skeleton, Stack } from '@chakra-ui/react';
import { useQueryStates } from 'nuqs';
import { Suspense, useCallback, useRef } from 'react';
import { isUuid } from '@/libs/utils/is-uuid';
import {
  CONTACT_DRILLDOWN_RESET,
  contactDrawerParsers,
  type ContactPlatform,
} from '../contact-drawer-url-state';
import type { ContactShell } from '../contact-types';
import { CONTACT_VIEW_REGISTRY } from '../contact-views';
import { effectiveContactPlatform } from '../platform-of';
import { useContactAdComposeTitle } from '../views/ads/use-contact-ad-compose-title';
import ContactProfileSkeleton from '../views/profile-view/ContactProfileSkeleton';
import ContactContextBar from './ContactContextBar';

interface ContactDetailPanelProps {
  contact: ContactShell;
  onClose: () => void;
  /**
   * Stacked mobile layout: force the context-bar back affordance and route it
   * to pop back to the rail screen instead of resetting to the profile view.
   */
  forceBack?: boolean;
  onBack?: () => void;
}

/** Lightweight skeleton shown while a view's lazy bundle loads. */
const ViewSkeleton: React.FC = () => (
  <Stack gap="3" p="22px 26px">
    <Skeleton height="14px" width="120px" />
    <Skeleton height="80px" rounded="lg" />
    <Skeleton height="80px" rounded="lg" />
  </Stack>
);

/**
 * Right-hand detail panel. Independent vertical scroll. Renders the sticky
 * context bar, then the active view from the lazy registry inside a single
 * Suspense boundary. View + platform come from URL state so the panel is
 * addressable and survives reloads.
 */
const ContactDetailPanel: React.FC<ContactDetailPanelProps> = ({
  contact,
  onClose,
  forceBack = false,
  onBack,
}) => {
  const [
    {
      contactView,
      contactPlatform,
      contactCollab,
      contactDeliverable,
      contactAd,
      contactAdCompose,
    },
    setParams,
  ] = useQueryStates(contactDrawerParsers);

  // The URL platform is what the user clicked; the contact may not have a profile
  // on it (e.g. a TikTok-only affiliate opened with the default 'ig'). Display the
  // effective platform everywhere in the panel — title, profile slice, and toggle
  // highlight all stay in sync — without rewriting the URL.
  const effectivePlatform = effectiveContactPlatform(
    contactPlatform,
    contact.profiles.map((p) => p.platform)
  );

  // Null on every other screen, so the bar keeps its own title.
  const adComposeTitle = useContactAdComposeTitle();

  const headerActionsRef = useRef<HTMLDivElement>(null);
  const ActiveView = CONTACT_VIEW_REGISTRY[contactView];
  // Outreach is a self-contained email composer that fills the panel and scrolls
  // its own body, so the panel itself must not scroll (which would double-scroll
  // under the sticky context bar). Other views keep the panel's own scroll.
  const isOutreach = contactView === 'outreach';

  const handleBack = useCallback(() => {
    // Deepest first, one level per click: composer -> ad detail -> list.
    if (contactAdCompose) {
      void setParams({ contactAdCompose: null });
      return;
    }
    if (contactAd) {
      void setParams({ contactAd: null, contactAdCompose: null });
      return;
    }
    if (contactDeliverable) {
      void setParams({ contactDeliverable: null });
      return;
    }
    // One level up, not straight to the profile: a real drill-in id (collab
    // detail, program manage) first returns to its view's list. The rail seeds
    // `contactCollab` with keyword values too ("collabs"), which are not a
    // drill-in — only a UUID counts.
    if (isUuid(contactCollab)) {
      void setParams({ contactCollab: null, contactDeliverable: null });
      return;
    }
    if (onBack) {
      onBack();
      return;
    }
    void setParams({ ...CONTACT_DRILLDOWN_RESET, contactView: 'profile' });
  }, [onBack, contactAd, contactAdCompose, contactCollab, contactDeliverable, setParams]);

  const handlePlatformChange = useCallback(
    (platform: ContactPlatform) => {
      void setParams({
        ...CONTACT_DRILLDOWN_RESET,
        contactPlatform: platform,
        contactView: 'profile',
      });
    },
    [setParams]
  );

  const view = (
    <Suspense fallback={contactView === 'profile' ? <ContactProfileSkeleton /> : <ViewSkeleton />}>
      <ActiveView
        headerActionsRef={headerActionsRef}
        contact={contact}
        platform={effectivePlatform}
        onClose={onClose}
        onBack={handleBack}
      />
    </Suspense>
  );

  const isCollabDrillIn = contactView === 'collabs' && isUuid(contactCollab);
  const ownsScroll = isOutreach || isCollabDrillIn;

  return (
    <Box
      flex="1"
      minW="0"
      h="100%"
      bg="panelBg"
      position="relative"
      display={ownsScroll ? 'flex' : undefined}
      flexDirection={ownsScroll ? 'column' : undefined}
      overflowY={ownsScroll ? 'hidden' : 'auto'}
    >
      {!isCollabDrillIn && (
        <ContactContextBar
          contact={contact}
          view={contactView}
          platform={effectivePlatform}
          onPlatformChange={handlePlatformChange}
          onBack={handleBack}
          onClose={onClose}
          forceBack={forceBack}
          titleOverride={adComposeTitle}
          actions={
            contactView === 'affiliation' && isUuid(contactCollab) ? (
              <Box ref={headerActionsRef} flex="none" />
            ) : undefined
          }
        />
      )}
      {ownsScroll ? (
        <Box flex="1" minH="0" display="flex" flexDirection="column">
          {view}
        </Box>
      ) : (
        view
      )}
    </Box>
  );
};

export default ContactDetailPanel;
